import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import useApiRequest from '../../../../../lib/hooks/useRequest';
import { SelfCheckoutCodeType } from '../../../../../lib/interfaces/organisation';
import { Button } from '../../../../../ui/components/ui/button';
import DataFetchParent from '../../../../../ui/components/ui/data-fetch';
import { Form } from '../../../../../ui/components/ui/form';
import { useIbanValidation } from '../../../../organisations/hooks/use-iban-validation';
import {
  createBasePaymentDataSchema,
  createCheckoutPaymentDataSchema,
  createDataSchemaWithIbanValidation
} from '../../../../organisations/root/forms/data-schemas/payment-data-schema';
import { PersonalDataSchemaType } from '../../../../organisations/root/forms/data-schemas/personal-data-schema';
import PaymentFormItems from '../../../../organisations/root/forms/items/payment-form-items';
import { PackageDisplayConfig } from '../config/package-mapper';
import { BillingFrequency } from '../config/packages-display.config';
import { PackageCategory } from '../enums/package-category.enum';
import { CreateSelfCheckoutRequestDto } from '../types/request';
import { CapacityMetaResponseDto } from '../types/response';

interface PaymentFormFetcherProps {
  capacities: CapacityMetaResponseDto;
  packages: Record<PackageCategory, PackageDisplayConfig>;
  selectedPlan: string;
  selectedBillingFrequency: BillingFrequency;
  setSelectedBillingFrequency: (frequency: BillingFrequency) => void;
  selectedTier: PackageCategory;
  setSelectedTier: (tier: PackageCategory) => void;
  setActiveCodeType: (codeType: SelfCheckoutCodeType | undefined) => void;
}

interface PaymentFormProps {
  personalData: PersonalDataSchemaType;
  capacities: CapacityMetaResponseDto;
  packages: Record<PackageCategory, PackageDisplayConfig>;
  selectedPlan: string;
  selectedBillingFrequency: BillingFrequency;
  setSelectedBillingFrequency: (frequency: BillingFrequency) => void;
  selectedTier: PackageCategory;
  setSelectedTier: (tier: PackageCategory) => void;
  setActiveCodeType: (codeType: SelfCheckoutCodeType | undefined) => void;
}

const PaymentFormFetcher = ({
  capacities,
  packages,
  selectedPlan,
  selectedBillingFrequency,
  setSelectedBillingFrequency,
  selectedTier,
  setSelectedTier,
  setActiveCodeType
}: PaymentFormFetcherProps) => {
  const [item, setItem] = useState<{
    personalData: PersonalDataSchemaType;
    capacities: CapacityMetaResponseDto;
    packages: Record<PackageCategory, PackageDisplayConfig>;
  }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const getCapacities = useCallback(async () => {
    setIsLoading(true);
    const personalDataString = localStorage.getItem('selfCheckoutPersonalData');
    let personalData: PersonalDataSchemaType | undefined;
    if (!personalDataString) {
      navigate('/self-checkout/step1');
    } else {
      personalData = JSON.parse(personalDataString) as PersonalDataSchemaType;
      setItem({ personalData, capacities, packages });
    }
    setIsLoading(false);
  }, [capacities, packages, navigate]);

  useEffect(() => {
    getCapacities();
  }, [getCapacities]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={item}
      renderElement={(data) => (
        <PaymentForm
          personalData={data.personalData}
          capacities={data.capacities}
          packages={data.packages}
          selectedPlan={selectedPlan}
          selectedBillingFrequency={selectedBillingFrequency}
          setSelectedBillingFrequency={setSelectedBillingFrequency}
          selectedTier={selectedTier}
          setSelectedTier={setSelectedTier}
          setActiveCodeType={setActiveCodeType}
        />
      )}
    />
  );
};

const PaymentForm = ({
  personalData,
  capacities,
  packages,
  selectedPlan,
  selectedBillingFrequency,
  setSelectedBillingFrequency,
  selectedTier,
  setSelectedTier,
  setActiveCodeType
}: PaymentFormProps) => {
  const navigate = useNavigate();
  const { apiRequest } = useApiRequest();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isGermany = personalData.country === 'DE';

  const { validateIban, isValidating, ibanValid, bicValid } = useIbanValidation({
    apiRoute: 'self-checkout/iban-validation',
    apiRequest
  });

  // Create payment schema with IBAN validation
  const basePaymentDataSchema = createBasePaymentDataSchema();
  const checkoutPaymentDataSchema = createCheckoutPaymentDataSchema(packages);
  const paymentDataSchema = z.intersection(basePaymentDataSchema, checkoutPaymentDataSchema);
  const paymentDataSchemaWithIbanValidation = createDataSchemaWithIbanValidation(paymentDataSchema, {
    validateIban,
    country: personalData.country
    // kein forceUseInvoice → wird aus data.codeType abgeleitet
  });
  type PaymentDataSchema = z.infer<typeof paymentDataSchemaWithIbanValidation>;

  const form = useForm<PaymentDataSchema>({
    resolver: zodResolver(paymentDataSchemaWithIbanValidation),
    mode: 'onBlur',
    defaultValues: {
      iban: '',
      bic: '',
      invoiceMail: '',
      newCustomerDate: '',
      package: selectedPlan,
      termsOfService: false,
      paymentConfirmation: false,
      minimumDuration: false,
      code: '',
      codeValid: false,
      codeType: undefined
    }
  });

  // Trigger validation when form values change, except for IBAN and BIC which are validated on blur
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      // Skip IBAN and BIC validation on change, they will be validated on blur
      if (name !== 'iban' && name !== 'bic') {
        form.trigger(name as keyof PaymentDataSchema);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (data: PaymentDataSchema) => {
    try {
      setIsSubmitting(true);

      // Combine data from both steps
      const requestData: CreateSelfCheckoutRequestDto = {
        ...personalData,
        ...data,
        package: selectedPlan,
        invoiceMail: data.invoiceMail !== '' ? data.invoiceMail : undefined,
        bic: data.bic !== '' ? data.bic : undefined,
        iban: data.iban !== '' ? data.iban : undefined,
        code: data.code && data.codeValid ? data.code : undefined
      };

      // Make API request
      const response = await apiRequest('self-checkout', 'POST', {
        body: requestData
      });

      if (response.status === 201) {
        // Clear localStorage
        localStorage.removeItem('selfCheckoutPersonalData');

        // Navigate to success page or dashboard
        navigate('/self-checkout/step3');
      } else {
        // Handle error
        console.error('Failed to submit form:', response.error);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (form.getValues('newCustomerDate') !== '') form.setValue('newCustomerDate', '', { shouldValidate: true });
  }, [selectedPlan, form]);

  return (
    <div className="flex flex-col gap-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full justify-around">
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="text-2xl font-semibold">Zahlungsmethode</h3>
              <p className="text-gray-400 text-xs">Bitte trage Deine Zahlungsmethode unten ein.</p>
            </div>
            <PaymentFormItems
              form={form}
              isValidating={isValidating}
              ibanValid={ibanValid}
              bicValid={bicValid}
              isGermany={isGermany}
              selectedBillingFrequency={selectedBillingFrequency}
              setSelectedBillingFrequency={setSelectedBillingFrequency}
              packages={packages}
              selectedTier={selectedTier}
              setSelectedTier={setSelectedTier}
              setActiveCodeType={setActiveCodeType}
              capacities={capacities}
            />

            <div className="flex gap-3 flex-col mt-8">
              <Button type="submit" className="w-full" disabled={isSubmitting || !form.formState.isValid}>
                {isSubmitting ? 'Wird gesendet...' : 'Jetzt mit 7i7® verbindlich durchstarten'}
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={() => navigate('/self-checkout/step1')}>
                Zurück zu persönlichen Daten
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default PaymentFormFetcher;
